import React, { createContext, useContext, useRef, ReactNode } from 'react';

interface AccessibilityContextType {
  announceToScreenReader: (message: string, priority?: 'polite' | 'assertive') => void;
  announcePageChange: (pageName: string) => void;
  announceLoadingState: (isLoading: boolean, message?: string) => void;
  announceError: (error: string) => void;
  announceSuccess: (message: string) => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

interface AccessibilityProviderProps {
  children: ReactNode;
}

/**
 * WCAG 4.1.3 - Status Messages
 * Provides screen reader announcements for dynamic content changes
 */
export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const politeAnnouncerRef = useRef<HTMLDivElement>(null);
  const assertiveAnnouncerRef = useRef<HTMLDivElement>(null);

  const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcer = priority === 'assertive' ? assertiveAnnouncerRef.current : politeAnnouncerRef.current;
    
    if (announcer) {
      // Clear previous message
      announcer.textContent = '';
      
      // Add new message after a brief delay to ensure screen readers pick it up
      setTimeout(() => {
        announcer.textContent = message;
      }, 100);
      
      // Clear message after 5 seconds to prevent accumulation
      setTimeout(() => {
        if (announcer.textContent === message) {
          announcer.textContent = '';
        }
      }, 5000);
    }
  };

  const announcePageChange = (pageName: string) => {
    announceToScreenReader(`Navigated to ${pageName} page`, 'polite');
  };

  const announceLoadingState = (isLoading: boolean, message?: string) => {
    if (isLoading) {
      announceToScreenReader(message || 'Loading content, please wait', 'polite');
    } else {
      announceToScreenReader('Content loaded', 'polite');
    }
  };

  const announceError = (error: string) => {
    announceToScreenReader(`Error: ${error}`, 'assertive');
  };

  const announceSuccess = (message: string) => {
    announceToScreenReader(`Success: ${message}`, 'polite');
  };

  return (
    <AccessibilityContext.Provider
      value={{
        announceToScreenReader,
        announcePageChange,
        announceLoadingState,
        announceError,
        announceSuccess,
      }}
    >
      {children}
      
      {/* WCAG 4.1.3 - Live regions for screen reader announcements */}
      <div
        ref={politeAnnouncerRef}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
      <div
        ref={assertiveAnnouncerRef}
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = (): AccessibilityContextType => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};
